import type { MapDocument } from "./types";

/** Hiding a location suppresses navigation, not the physical booth it belongs to. */
export function isMapLocationHidden(
  document: MapDocument,
  id: string,
): boolean {
  return (
    document.locations.some(
      (location) => location.id === id && location.hidden === true,
    ) ||
    document.geometry.stands.some(
      (stand) => stand.id === id && stand.hidden === true,
    )
  );
}

const presentations = new WeakMap<MapDocument, MapDocument>();

/** Display-only projection. Keep the original document for editing and routing obstacles. */
export function visibleMapDocument(document: MapDocument): MapDocument {
  const cached = presentations.get(document);
  if (cached) return cached;
  const hiddenBooths = new Set(
    document.geometry.stands
      .filter((stand) => stand.hidden)
      .map((stand) => stand.id),
  );
  const hiddenLocations = new Set(
    document.locations
      .filter((location) => location.hidden)
      .map((location) => location.id),
  );
  if (!hiddenBooths.size && !hiddenLocations.size) return document;
  const visible = {
    ...document,
    geometry: {
      ...document.geometry,
      stands: document.geometry.stands.filter(
        (stand) => !hiddenBooths.has(stand.id),
      ),
      entrances: document.geometry.entrances.filter(
        (entrance) => !hiddenLocations.has(entrance.id),
      ),
    },
    locations: document.locations.filter(
      (location) =>
        !hiddenLocations.has(location.id) && !hiddenBooths.has(location.id),
    ),
  };
  presentations.set(document, visible);
  return visible;
}

const previewPresentations = new WeakMap<MapDocument, MapDocument>();
const previewOpacities = new WeakMap<object, number>();

/** Transient editor styling, deliberately absent from the saved/public map contract. */
export function mapPreviewOpacity(item: object): number {
  return previewOpacities.get(item) ?? 1;
}

/** Preserve editor styling when another display-only effect clones a booth. */
export function inheritMapPreviewOpacity(source: object, target: object): void {
  const opacity = previewOpacities.get(source);
  if (opacity !== undefined) previewOpacities.set(target, opacity);
}

/**
 * The editor can inspect hidden features without changing their saved visibility.
 * Every changed object is cloned; unhidden flags and opacity belong only to this
 * display projection. Routing must continue using the original/public document.
 */
export function previewHiddenMapDocument(document: MapDocument): MapDocument {
  const cached = previewPresentations.get(document);
  if (cached) return cached;
  const hiddenBooths = new Set(
    document.geometry.stands
      .filter((stand) => stand.hidden)
      .map((stand) => stand.id),
  );
  const hiddenLocations = new Set(
    document.locations
      .filter((location) => location.hidden)
      .map((location) => location.id),
  );
  if (!hiddenBooths.size && !hiddenLocations.size) return document;
  const faded = <T extends object>(item: T): T => {
    previewOpacities.set(item, 0.5);
    return item;
  };
  const preview = {
    ...document,
    geometry: {
      ...document.geometry,
      stands: document.geometry.stands.map((stand) =>
        stand.hidden ? faded({ ...stand, hidden: false }) : stand,
      ),
      entrances: document.geometry.entrances.map((entrance) =>
        hiddenLocations.has(entrance.id) || hiddenBooths.has(entrance.id)
          ? faded({ ...entrance })
          : entrance,
      ),
    },
    locations: document.locations.map((location) =>
      hiddenLocations.has(location.id) || hiddenBooths.has(location.id)
        ? faded({ ...location, hidden: false })
        : location,
    ),
  };
  previewPresentations.set(document, preview);
  return preview;
}
